import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, withModifiers as _withModifiers, normalizeClass as _normalizeClass, renderList as _renderList, createBlock as _createBlock } from "vue"

const _hoisted_1 = {
  class: "collection-excel-upload-modal",
  "data-tour": "bb-test-collection-excel-dialog"
}
const _hoisted_2 = { class: "modal-header" }
const _hoisted_3 = { class: "header-left" }
const _hoisted_4 = { class: "modal-body" }
const _hoisted_5 = { class: "upload-notice" }
const _hoisted_6 = { class: "section" }
const _hoisted_7 = { class: "month-selector" }
const _hoisted_8 = { class: "section" }
const _hoisted_9 = { class: "section-content" }
const _hoisted_10 = { class: "section" }
const _hoisted_11 = { class: "section-content" }
const _hoisted_12 = { class: "file-name" }
const _hoisted_13 = { class: "file-size" }
const _hoisted_14 = {
  key: 0,
  class: "section"
}
const _hoisted_15 = { class: "section-title" }
const _hoisted_16 = { class: "preview-count" }
const _hoisted_17 = { class: "preview-table-wrap" }
const _hoisted_18 = { class: "preview-table" }
const _hoisted_19 = { class: "text-right" }
const _hoisted_20 = {
  key: 0,
  class: "more-row"
}
const _hoisted_21 = { colspan: "6" }
const _hoisted_22 = {
  key: 1,
  class: "section"
}
const _hoisted_23 = { class: "result-box error" }
const _hoisted_24 = { class: "result-content" }
const _hoisted_25 = { class: "result-message" }
const _hoisted_26 = { class: "modal-footer" }
const _hoisted_27 = ["disabled"]

import { ref, computed, watch } from 'vue';
import * as XLSX from 'xlsx';
import { collectionApi } from '@/services/api';

interface Props {
  modelValue: boolean;
  memberCode: string | number;
  year?: number | string;
  month?: number | string;
  bizGubun?: string;
}

interface PreviewItem {
  name: string;
  ltcNo?: string;
  itemName: string;
  charge: number;
  chargeDate?: string;
  memo?: string;
}
interface UploadResult {
  success: boolean;
  message: string;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'CollectionExcelUploadModal',
  props: {
    modelValue: { type: Boolean },
    memberCode: {},
    year: { default: () => new Date().getFullYear() },
    month: { default: () => new Date().getMonth() + 1 },
    bizGubun: {}
  },
  emits: ["update:modelValue", "uploaded"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

// 다이얼로그 상태
const dialogOpen = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
});

// 연월 선택
const selectedMonth = ref('');

// 파일 업로드 관련
const fileInputRef = ref<HTMLInputElement | null>(null);
const uploadedFile = ref<File | null>(null);
const isDragging = ref(false);
const isUploading = ref(false);

// 미리보기 데이터
const previewData = ref<PreviewItem[]>([]);

// 업로드 결과 (에러 표시용)
const uploadResult = ref<UploadResult | null>(null);

// 업로드 가능 여부
const canUpload = computed(() => {
  return uploadedFile.value && previewData.value.length > 0 && selectedMonth.value;
});

// 초기화
watch(() => props.modelValue, (val) => {
  if (val) {
    // 모달 열릴 때 초기화
    const y = typeof props.year === 'string' ? parseInt(props.year) : (props.year || new Date().getFullYear());
    const m = typeof props.month === 'string' ? parseInt(props.month) : (props.month || new Date().getMonth() + 1);
    selectedMonth.value = `${y}-${String(m).padStart(2, '0')}`;
    uploadedFile.value = null;
    previewData.value = [];
    uploadResult.value = null;
  }
});

// 다이얼로그 닫기
const closeDialog = () => {
  dialogOpen.value = false;
};

// 파일 선택 트리거
const triggerFileInput = () => {
  fileInputRef.value?.click();
};

// 파일 선택 핸들러
const handleFileSelect = (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (input.files && input.files[0]) {
    processFile(input.files[0]);
  }
};

// 드롭 핸들러
const handleDrop = (event: DragEvent) => {
  isDragging.value = false;
  const files = event.dataTransfer?.files;
  if (files && files[0]) {
    processFile(files[0]);
  }
};

// 파일 처리
const processFile = async (file: File) => {
  // 확장자 체크
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!['xlsx', 'xls'].includes(ext || '')) {
    alert('xlsx 또는 xls 파일만 업로드 가능합니다.');
    return;
  }
  
  uploadedFile.value = file;
  uploadResult.value = null;
  
  // 엑셀 파싱
  try {
    const data = await readExcelFile(file);
    previewData.value = data;
  } catch (error) {
    console.error('엑셀 파싱 오류:', error);
    alert('엑셀 파일을 읽는 중 오류가 발생했습니다.');
    uploadedFile.value = null;
    previewData.value = [];
  }
};

// 엑셀 파일 읽기
const readExcelFile = (file: File): Promise<PreviewItem[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
        
        // 첫 번째 행은 헤더, 두 번째 행부터 데이터
        const items: PreviewItem[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || !row[0]) continue; // 빈 행 스킵
          
          const name = String(row[0] || '').trim();
          const ltcNo = String(row[1] || '').trim();
          const itemName = String(row[2] || '').trim();
          const charge = parseNumber(row[3]);
          const chargeDate = formatExcelDate(row[4]);
          const memo = String(row[5] || '').trim();
          
          if (name && itemName) {
            items.push({ name, ltcNo, itemName, charge, chargeDate, memo });
          }
        }
        
        resolve(items);
      } catch (error) {
        reject(error);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
};

// 엑셀 날짜 변환
const formatExcelDate = (value: any): string => {
  if (!value) return '';
  
  // 이미 문자열인 경우
  if (typeof value === 'string') {
    // YYYY-MM-DD 형식 체크
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    // YYYY/MM/DD 형식
    if (/^\d{4}\/\d{2}\/\d{2}$/.test(value)) return value.replace(/\//g, '-');
    return value;
  }
  
  // 엑셀 날짜 숫자인 경우
  if (typeof value === 'number') {
    const date = XLSX.SSF.parse_date_code(value);
    if (date) {
      return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
    }
  }
  
  return '';
};

// 파일 제거
const removeFile = () => {
  uploadedFile.value = null;
  previewData.value = [];
  uploadResult.value = null;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
};

// 양식 다운로드
const downloadTemplate = () => {
  // 양식 데이터 생성
  const templateData = [
    ['수급자명', '인정관리번호', '항목', '청구금액', '청구일', '비고'],
    ['홍길동', 'L2312017237', '본인부담금수입', 150000, '2025-01-15', '1월분'],
    ['홍길동', 'L2312017237', '식재료비수입', 50000, '2025-01-15', ''],
    ['김철수', 'L2401005123', '본인부담금수입', 120000, '2025-01-15', '1월분'],
  ];
  
  const ws = XLSX.utils.aoa_to_sheet(templateData);
  
  // 컬럼 너비 설정
  ws['!cols'] = [
    { wch: 12 },  // 수급자명
    { wch: 15 },  // 인정관리번호
    { wch: 18 },  // 항목
    { wch: 12 },  // 청구금액
    { wch: 12 },  // 청구일
    { wch: 20 },  // 비고
  ];
  
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, '수납관리');
  
  XLSX.writeFile(wb, '수납관리_양식.xlsx');
};

// 데이터 업로드
const uploadData = async () => {
  if (!canUpload.value) return;
  
  const [year, month] = selectedMonth.value.split('-').map(Number);
  
  isUploading.value = true;
  uploadResult.value = null;
  
  try {
    const response = await collectionApi.uploadExcel(props.memberCode, {
      items: previewData.value,
      year,
      month,
      bizGubun: props.bizGubun
    });
    
    if (response.data?.success) {
      const data = response.data.data;
      
      // 결과 메시지 생성
      const resultParts = [];
      if (data?.newBeneficiaries > 0) {
        resultParts.push(`신규 수급자 ${data.newBeneficiaries}명 등록`);
      }
      if (data?.insertedCollections > 0) {
        resultParts.push(`수납관리 ${data.insertedCollections}건 등록`);
      }
      
      const resultMessage = resultParts.length > 0 
        ? `업로드 완료!\n${resultParts.join('\n')}`
        : '업로드가 완료되었습니다.';
      
      alert(resultMessage);
      
      // 성공 시 부모 컴포넌트에 알림 후 모달 닫기
      emit('uploaded');
      closeDialog();
    } else {
      uploadResult.value = {
        success: false,
        message: response.data?.message || '업로드 중 오류가 발생했습니다.'
      };
    }
  } catch (error: any) {
    uploadResult.value = {
      success: false,
      message: error.response?.data?.message || error.message || '업로드 중 오류가 발생했습니다.'
    };
  } finally {
    isUploading.value = false;
  }
};

// 숫자 파싱
const parseNumber = (value: any): number => {
  if (!value) return 0;
  if (typeof value === 'number') return value;
  const num = parseInt(String(value).replace(/[^0-9-]/g, ''));
  return isNaN(num) ? 0 : num;
};

// 숫자 포맷
const formatNumber = (value: number): string => {
  return value.toLocaleString('ko-KR');
};

// 파일 크기 포맷
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_dialog, {
    modelValue: dialogOpen.value,
    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialogOpen).value = $event)),
    "max-width": "700",
    persistent: ""
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(_component_v_icon, { size: "20" }, {
              default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                _createTextVNode("mdi-file-excel", -1)
              ]))]),
              _: 1
            }),
            _cache[5] || (_cache[5] = _createElementVNode("span", { class: "title" }, "수납관리 엑셀 업로드", -1))
          ]),
          _createElementVNode("button", {
            class: "btn-close",
            onClick: closeDialog
          }, [
            _createVNode(_component_v_icon, { size: "18" }, {
              default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                _createTextVNode("mdi-close", -1)
              ]))]),
              _: 1
            })
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(_component_v_icon, { size: "16" }, {
              default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                _createTextVNode("mdi-information-outline", -1)
              ]))]),
              _: 1
            }),
            _cache[8] || (_cache[8] = _createElementVNode("p", null, [
              _createTextVNode("이 업로드는 수급자별 "),
              _createElementVNode("strong", null, "청구"),
              _createTextVNode("를 등록해 "),
              _createElementVNode("strong", null, "미수금"),
              _createTextVNode("을 관리하는 용도입니다. 통장에 입금된 돈은 통장내역에서 관리하고, 여기서는 청구와 입금을 맞춰 미수만 확인합니다.")
            ], -1))
          ]),
          _createElementVNode("div", _hoisted_6, [
            _cache[9] || (_cache[9] = _createElementVNode("div", { class: "section-title" }, "업로드 대상 연월", -1)),
            _createElementVNode("div", _hoisted_7, [
              _withDirectives(_createElementVNode("input", {
                type: "month",
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedMonth).value = $event)),
                class: "month-input"
              }, null, 512), [
                [_vModelText, selectedMonth.value]
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_8, [
            _cache[13] || (_cache[13] = _createElementVNode("div", { class: "section-title" }, "1. 엑셀 양식 다운로드", -1)),
            _createElementVNode("div", _hoisted_9, [
              _cache[12] || (_cache[12] = _createElementVNode("p", { class: "description" }, "아래 양식을 다운로드하여 수납관리 데이터를 작성하세요.", -1)),
              _createElementVNode("button", {
                class: "btn-download",
                onClick: downloadTemplate
              }, [
                _createVNode(_component_v_icon, { size: "16" }, {
                  default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                    _createTextVNode("mdi-download", -1)
                  ]))]),
                  _: 1
                }),
                _cache[11] || (_cache[11] = _createElementVNode("span", null, "양식 다운로드", -1))
              ])
            ])
          ]),
          _cache[25] || (_cache[25] = _createElementVNode("div", { class: "section" }, [
            _createElementVNode("div", { class: "section-title" }, "양식 설명"),
            _createElementVNode("div", { class: "template-info" }, [
              _createElementVNode("table", { class: "info-table" }, [
                _createElementVNode("thead", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("th", null, "컬럼"),
                    _createElementVNode("th", null, "설명"),
                    _createElementVNode("th", null, "필수")
                  ])
                ]),
                _createElementVNode("tbody", null, [
                  _createElementVNode("tr", null, [
                    _createElementVNode("td", null, "수급자명"),
                    _createElementVNode("td", null, "수급자 이름 (미등록시 자동 등록)"),
                    _createElementVNode("td", { class: "required" }, "필수")
                  ]),
                  _createElementVNode("tr", null, [
                    _createElementVNode("td", null, "인정관리번호"),
                    _createElementVNode("td", null, "장기요양인정번호 (예: L2312017237)"),
                    _createElementVNode("td", null, "선택")
                  ]),
                  _createElementVNode("tr", null, [
                    _createElementVNode("td", null, "항목"),
                    _createElementVNode("td", null, "본인부담금수입, 식재료비수입, 상급침실이용료, 이미용비, 기타비급여수입"),
                    _createElementVNode("td", { class: "required" }, "필수")
                  ]),
                  _createElementVNode("tr", null, [
                    _createElementVNode("td", null, "청구금액"),
                    _createElementVNode("td", null, "청구할 금액 (숫자만)"),
                    _createElementVNode("td", { class: "required" }, "필수")
                  ]),
                  _createElementVNode("tr", null, [
                    _createElementVNode("td", null, "청구일"),
                    _createElementVNode("td", null, "청구 날짜 (YYYY-MM-DD 형식)"),
                    _createElementVNode("td", null, "선택")
                  ]),
                  _createElementVNode("tr", null, [
                    _createElementVNode("td", null, "비고"),
                    _createElementVNode("td", null, "메모"),
                    _createElementVNode("td", null, "선택")
                  ])
                ])
              ])
            ])
          ], -1)),
          _createElementVNode("div", _hoisted_10, [
            _cache[20] || (_cache[20] = _createElementVNode("div", { class: "section-title" }, "2. 엑셀 파일 업로드", -1)),
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("div", {
                class: _normalizeClass(["upload-area", { 'dragging': isDragging.value, 'has-file': uploadedFile.value }]),
                onDragover: _cache[1] || (_cache[1] = _withModifiers(($event: any) => (isDragging.value = true), ["prevent"])),
                onDragleave: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (isDragging.value = false), ["prevent"])),
                onDrop: _withModifiers(handleDrop, ["prevent"]),
                onClick: triggerFileInput
              }, [
                _createElementVNode("input", {
                  type: "file",
                  ref_key: "fileInputRef",
                  ref: fileInputRef,
                  accept: ".xlsx,.xls",
                  onChange: handleFileSelect,
                  style: {"display":"none"}
                }, null, 544),
                (!uploadedFile.value)
                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                      _createVNode(_component_v_icon, {
                        size: "40",
                        color: "#94a3b8"
                      }, {
                        default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                          _createTextVNode("mdi-file-upload-outline", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[15] || (_cache[15] = _createElementVNode("p", { class: "upload-text" }, "클릭하거나 파일을 여기에 드롭하세요", -1)),
                      _cache[16] || (_cache[16] = _createElementVNode("p", { class: "upload-hint" }, ".xlsx, .xls 파일만 가능", -1))
                    ], 64))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createVNode(_component_v_icon, {
                        size: "32",
                        color: "#059669"
                      }, {
                        default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                          _createTextVNode("mdi-file-excel", -1)
                        ]))]),
                        _: 1
                      }),
                      _createElementVNode("p", _hoisted_12, _toDisplayString(uploadedFile.value.name), 1),
                      _createElementVNode("p", _hoisted_13, _toDisplayString(formatFileSize(uploadedFile.value.size)), 1),
                      _createElementVNode("button", {
                        class: "btn-remove-file",
                        onClick: _withModifiers(removeFile, ["stop"])
                      }, [
                        _createVNode(_component_v_icon, { size: "14" }, {
                          default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                            _createTextVNode("mdi-close", -1)
                          ]))]),
                          _: 1
                        }),
                        _cache[19] || (_cache[19] = _createTextVNode(" 파일 제거 ", -1))
                      ])
                    ], 64))
              ], 34)
            ])
          ]),
          (previewData.value.length > 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                _createElementVNode("div", _hoisted_15, [
                  _cache[21] || (_cache[21] = _createTextVNode(" 미리보기 ", -1)),
                  _createElementVNode("span", _hoisted_16, "(" + _toDisplayString(previewData.value.length) + "건)", 1)
                ]),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("table", _hoisted_18, [
                    _cache[22] || (_cache[22] = _createElementVNode("thead", null, [
                      _createElementVNode("tr", null, [
                        _createElementVNode("th", null, "수급자명"),
                        _createElementVNode("th", null, "인정관리번호"),
                        _createElementVNode("th", null, "항목"),
                        _createElementVNode("th", null, "청구금액"),
                        _createElementVNode("th", null, "청구일"),
                        _createElementVNode("th", null, "비고")
                      ])
                    ], -1)),
                    _createElementVNode("tbody", null, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(previewData.value.slice(0, 10), (row, idx) => {
                        return (_openBlock(), _createElementBlock("tr", { key: idx }, [
                          _createElementVNode("td", null, _toDisplayString(row.name), 1),
                          _createElementVNode("td", null, _toDisplayString(row.ltcNo || '-'), 1),
                          _createElementVNode("td", null, _toDisplayString(row.itemName), 1),
                          _createElementVNode("td", _hoisted_19, _toDisplayString(formatNumber(row.charge)), 1),
                          _createElementVNode("td", null, _toDisplayString(row.chargeDate || '-'), 1),
                          _createElementVNode("td", null, _toDisplayString(row.memo || '-'), 1)
                        ]))
                      }), 128)),
                      (previewData.value.length > 10)
                        ? (_openBlock(), _createElementBlock("tr", _hoisted_20, [
                            _createElementVNode("td", _hoisted_21, "... 외 " + _toDisplayString(previewData.value.length - 10) + "건 더 있음", 1)
                          ]))
                        : _createCommentVNode("", true)
                    ])
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
          (uploadResult.value && !uploadResult.value.success)
            ? (_openBlock(), _createElementBlock("div", _hoisted_22, [
                _cache[24] || (_cache[24] = _createElementVNode("div", { class: "section-title" }, "업로드 오류", -1)),
                _createElementVNode("div", _hoisted_23, [
                  _createVNode(_component_v_icon, { size: "20" }, {
                    default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                      _createTextVNode("mdi-alert-circle", -1)
                    ]))]),
                    _: 1
                  }),
                  _createElementVNode("div", _hoisted_24, [
                    _createElementVNode("p", _hoisted_25, _toDisplayString(uploadResult.value.message), 1)
                  ])
                ])
              ]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_26, [
          _createElementVNode("button", {
            class: "btn-cancel",
            onClick: closeDialog
          }, "닫기"),
          _createElementVNode("button", {
            class: "btn-upload",
            onClick: uploadData,
            disabled: !canUpload.value || isUploading.value
          }, [
            (isUploading.value)
              ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                  key: 0,
                  indeterminate: "",
                  size: "16",
                  width: "2"
                }))
              : (_openBlock(), _createBlock(_component_v_icon, {
                  key: 1,
                  size: "16"
                }, {
                  default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                    _createTextVNode("mdi-upload", -1)
                  ]))]),
                  _: 1
                })),
            _createElementVNode("span", null, _toDisplayString(isUploading.value ? '업로드 중...' : '업로드'), 1)
          ], 8, _hoisted_27)
        ])
      ])
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})